/**
 * AlimentariTab - Tab centralizat pentru TOATE alimentările
 * Include: alimentări de la șoferi + import CSV, vizualizare, filtre, alocare manuală, export Excel
 */
import { memo, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Fuel, Check, AlertTriangle, Search, User,
  CheckCircle, XCircle, Upload, RefreshCw, FileSpreadsheet, Trash2, Download, X, Copy, Car
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const MONTHS_RO = ['', 'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 
  'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];

// Status badge colors - this is in frontend, not here
const STATUS_CONFIG = {
  ok: { label: 'OK', color: 'bg-green-100 text-green-700', icon: Check },
  nealocat: { label: 'Nealocat', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
  reconciliat: { label: 'Reconciliat', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  dublat: { label: 'Dublat', color: 'bg-yellow-100 text-yellow-700', icon: Copy },
  verificat: { label: 'Verificat', color: 'bg-purple-100 text-purple-700', icon: CheckCircle }
};

const AlimentariImportTab = memo(function AlimentariImportTab() {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [statusFilter, setStatusFilter] = useState('toate');
  const [productFilter, setProductFilter] = useState('toate');
  const [sourceFilter, setSourceFilter] = useState('toate');
  const [carFilter, setCarFilter] = useState('toate');  // Filtru mașină
  const [searchQuery, setSearchQuery] = useState('');
  
  const [imports, setImports] = useState([]);
  const [stats, setStats] = useState({ total: 0, ok: 0, nealocat: 0, reconciliat: 0, dublat: 0, verificat: 0 });
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  
  const [cars, setCars] = useState([]);
  const [selectedCarId, setSelectedCarId] = useState({});
  
  // Upload state
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [csvPreviewData, setCsvPreviewData] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewFilter, setPreviewFilter] = useState(null);
  
  // Batch management
  const [batches, setBatches] = useState([]);
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [deletingBatch, setDeletingBatch] = useState(null);

  // Fetch all fuelings
  const fetchImports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        year: selectedYear,
        month: selectedMonth,
        ...(statusFilter !== 'toate' && { status: statusFilter }),
        ...(productFilter !== 'toate' && { product: productFilter }),
        ...(sourceFilter !== 'toate' && { source: sourceFilter })
      });
      
      const response = await axios.get(`${API}/fuel-imports?${params}`, getAuthHeaders());
      setImports(Array.isArray(response.data?.imports) ? response.data.imports : []);
      setStats(response.data?.stats || { total: 0, ok: 0, nealocat: 0, reconciliat: 0, dublat: 0, verificat: 0 });
    } catch (error) {
      console.error('Error fetching imports:', error);
      if (error.response?.status !== 404) {
        toast.error('Eroare la încărcarea alimentărilor');
      }
      setImports([]);
      setStats({ total: 0, ok: 0, nealocat: 0, reconciliat: 0, dublat: 0, verificat: 0 });
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, statusFilter, productFilter, sourceFilter]);

  // Fetch cars and products
  const fetchCars = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fuel-imports/cars`, getAuthHeaders());
      setCars(Array.isArray(response.data?.cars) ? response.data.cars : []);
    } catch (error) {
      console.error('Error fetching cars:', error);
      setCars([]);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fuel-imports/products`, getAuthHeaders());
      setProducts(Array.isArray(response.data?.products) ? response.data.products : []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    }
  }, []);

  const fetchBatches = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fuel-imports/batches`, getAuthHeaders());
      setBatches(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching batches:', error);
      setBatches([]);
    }
  }, []);

  useEffect(() => {
    fetchImports();
    fetchCars();
    fetchProducts();
    fetchBatches();
  }, [fetchImports, fetchCars, fetchProducts, fetchBatches]);

  // CSV Upload
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Te rog selectează un fișier CSV');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/import-fuel/upload`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setCsvPreviewData(response.data);
      setPreviewFilter(null);
      setShowPreviewDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la procesarea fișierului');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleConfirmImport = async () => {
    if (!csvPreviewData) return;
    
    setUploading(true);
    try {
      // Trimitem TOATE itemurile - backend-ul va determina statusul
      const allItems = csvPreviewData.preview || [];
      
      const response = await axios.post(`${API}/import-fuel/confirm`, {
        items: allItems
      }, getAuthHeaders());
      
      toast.success(response.data.message || 'Import finalizat');
      
      setShowPreviewDialog(false);
      setCsvPreviewData(null);
      
      // Refresh data
      fetchImports();
      fetchProducts();
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la import');
    } finally {
      setUploading(false);
    }
  };

  // Allocate fuel to car
  const handleAllocate = async (importId) => {
    const carId = selectedCarId[importId];
    if (!carId) {
      toast.error('Selectează o mașină');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/fuel-imports/${importId}/allocate`,
        { car_id: carId },
        getAuthHeaders()
      );
      
      toast.success(response.data.message || 'Alimentare alocată cu succes');
      setSelectedCarId(prev => ({ ...prev, [importId]: '' }));
      fetchImports();
    } catch (error) {
      toast.error('Eroare la alocare');
    }
  };

  // Delete single import
  const handleDeleteImport = async (importId) => {
    if (!window.confirm('Sigur vrei să ștergi această înregistrare?')) return;
    
    try {
      await axios.delete(`${API}/fuel-imports/${importId}`, getAuthHeaders());
      toast.success('Înregistrare ștearsă');
      fetchImports();
    } catch (error) {
      toast.error('Eroare la ștergere');
    }
  };

  // Delete batch
  const handleDeleteBatch = async (batchId) => {
    setDeletingBatch(batchId);
    try {
      const response = await axios.delete(`${API}/fuel-imports/batch/${batchId}`, getAuthHeaders());
      toast.success(response.data.message || 'Batch anulat');
      fetchImports();
      fetchBatches();
    } catch (error) {
      toast.error('Eroare la anularea importului');
    } finally {
      setDeletingBatch(null);
    }
  };

  // Clear ALL imports
  const handleClearAll = async () => {
    if (!window.confirm('⚠️ ATENȚIE: Aceasta va șterge TOATE alimentările importate!\n\nEști sigur?')) return;
    if (!window.confirm('Confirmă din nou - vrei să ștergi TOTUL?')) return;
    
    try {
      const response = await axios.delete(`${API}/fuel-imports/clear-all`, getAuthHeaders());
      toast.success(response.data.message || 'Toate datele au fost șterse');
      fetchImports();
      fetchBatches();
      fetchProducts();
      setShowBatchDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la ștergere');
    }
  };

  // Clear dublate și verificate
  const handleClearVerified = async () => {
    if (!window.confirm('Aceasta va șterge toate înregistrările cu status "Dublat" și "Verificat".\n\nEști sigur?')) return;
    
    try {
      const response = await axios.delete(`${API}/fuel-imports/clear-verified`, getAuthHeaders());
      toast.success(response.data.message || 'Înregistrările au fost șterse');
      fetchImports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la ștergere');
    }
  };

  // Marchează ca verificat
  const handleVerify = async (importId) => {
    try {
      await axios.post(`${API}/fuel-imports/${importId}/verify`, {}, getAuthHeaders());
      toast.success('Marcat ca verificat');
      fetchImports();
    } catch (error) {
      toast.error('Eroare la marcare');
    }
  };

  // Normalize products
  const handleNormalizeProducts = async () => {
    try {
      const response = await axios.post(`${API}/fuel-imports/normalize-products`, {}, getAuthHeaders());
      toast.success(response.data.message || 'Produse normalizate');
      fetchImports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la normalizare');
    }
  };

  // Export Excel
  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams({
        year: selectedYear,
        month: selectedMonth,
        ...(statusFilter !== 'toate' && { status: statusFilter }),
        ...(productFilter !== 'toate' && { product: productFilter })
      });
      
      const response = await axios.get(`${API}/fuel-imports/export?${params}`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Alimentari_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export Excel descărcat');
    } catch (error) {
      toast.error('Eroare la export');
    }
  };

  // Filter imports by search and car
  const filteredImports = imports.filter(imp => {
    // Filtru mașină
    if (carFilter !== 'toate' && imp.car_id !== carFilter) return false;
    
    // Filtru search
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      imp.nr_inmatriculare?.toLowerCase().includes(query) ||
      imp.statie?.toLowerCase().includes(query) ||
      imp.produs?.toLowerCase().includes(query) ||
      imp.car_name?.toLowerCase().includes(query)
    );
  });

  // Totaluri dinamice pe datele filtrate (grupate pe produs)
  const filteredTotals = useMemo(() => {
    const byProduct = {};
    let totalLitri = 0;
    let totalCost = 0;
    
    filteredImports.forEach(imp => {
      const produs = imp.produs || 'Altele';
      const litri = imp.litri || 0;
      const cost = imp.cost || 0;
      
      if (!byProduct[produs]) {
        byProduct[produs] = { litri: 0, cost: 0, count: 0 };
      }
      byProduct[produs].litri += litri;
      byProduct[produs].cost += cost;
      byProduct[produs].count += 1;
      
      totalLitri += litri;
      totalCost += cost;
    });
    
    return { byProduct, totalLitri, totalCost, count: filteredImports.length };
  }, [filteredImports]);

  return (
    <div className="space-y-4">
      {/* Header cu Upload */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Fuel className="w-5 h-5 text-green-600" />
            Alimentări
          </h3>
          <p className="text-sm text-slate-500">
            Toate alimentările - de la șoferi și din import CSV
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleFileSelect}
          />
          <Button 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-green-600 hover:bg-green-700"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Se procesează...' : 'Import CSV'}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowBatchDialog(true)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Istoric / Anulare
          </Button>
        </div>
      </div>

      {/* Period selector și Export */}
      <div className="flex flex-wrap items-center gap-3 p-3 bg-slate-50 rounded-lg">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-600">Perioadă:</span>
          <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
            <SelectTrigger className="w-[130px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS_RO.slice(1).map((month, idx) => (
                <SelectItem key={idx + 1} value={String(idx + 1)}>{month}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[90px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(year => (
                <SelectItem key={year} value={String(year)}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2 ml-auto">
          <Button variant="outline" size="sm" onClick={fetchImports} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportExcel}>
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="bg-slate-50 rounded-lg p-3 border">
          <div className="text-xs text-slate-500 font-medium">Total</div>
          <div className="text-2xl font-bold text-slate-800">{stats.total}</div>
          <div className="text-xs text-slate-400">{stats.total_litri?.toFixed(0) || 0} L</div>
        </div>
        <div className="bg-green-50 rounded-lg p-3 border border-green-200">
          <div className="text-xs text-green-600 font-medium flex items-center gap-1">
            <Check className="w-3 h-3" /> OK
          </div>
          <div className="text-2xl font-bold text-green-700">{stats.ok || 0}</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
          <div className="text-xs text-blue-600 font-medium flex items-center gap-1">
            <CheckCircle className="w-3 h-3" /> Reconciliat
          </div>
          <div className="text-2xl font-bold text-blue-700">{stats.reconciliat || 0}</div>
        </div>
        <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
          <div className="text-xs text-orange-600 font-medium flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Nealocat
          </div>
          <div className="text-2xl font-bold text-orange-700">{stats.nealocat || 0}</div>
        </div>
      </div>

      {/* Totaluri pe produs */}
      {stats.by_product && Object.keys(stats.by_product).length > 0 && (
        <div className="p-3 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border">
          <div className="text-xs font-medium text-slate-600 mb-2">Totaluri pe produs:</div>
          <div className="flex flex-wrap gap-4">
            {Object.entries(stats.by_product).map(([produs, data]) => (
              <div key={produs} className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-700">{produs}:</span>
                <span className="text-sm font-bold text-green-700">
                  {data.litri?.toFixed(1)} {produs.toLowerCase().includes('vign') ? 'buc' : 'L'}
                </span>
                <span className="text-xs text-slate-500">({data.count} alim.)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Caută nr. înmatriculare, stație, produs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="toate">Toate statusurile</SelectItem>
            <SelectItem value="ok">OK</SelectItem>
            <SelectItem value="reconciliat">Reconciliat</SelectItem>
            <SelectItem value="nealocat">Nealocat</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Sursa" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="toate">Toate sursele</SelectItem>
            <SelectItem value="sofer">Șofer</SelectItem>
            <SelectItem value="csv">Import CSV</SelectItem>
          </SelectContent>
        </Select>

        <Select value={productFilter} onValueChange={setProductFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Produs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="toate">Toate produsele</SelectItem>
            {/* Folosim produsele din stats.by_product sau din lista products */}
            {(stats.by_product ? Object.keys(stats.by_product) : products).map(prod => (
              <SelectItem key={prod} value={prod}>{prod}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={carFilter} onValueChange={setCarFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Mașină" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="toate">Toate mașinile</SelectItem>
            {cars.map(car => (
              <SelectItem key={car.id} value={car.id}>
                {car.license_plate} - {car.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Totaluri Dinamice pentru Selecția Curentă */}
      {(carFilter !== 'toate' || productFilter !== 'toate' || searchQuery.trim()) && (
        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2 mb-3">
            <Car className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-semibold text-blue-800">
              Totaluri pentru selecția curentă ({filteredTotals.count} înregistrări)
            </span>
          </div>
          <div className="flex flex-wrap gap-4">
            {Object.entries(filteredTotals.byProduct).map(([produs, data]) => (
              <div key={produs} className="bg-white px-3 py-2 rounded-md border shadow-sm">
                <span className="text-xs text-slate-500 block">{produs}</span>
                <span className="text-lg font-bold text-green-700">
                  {data.litri?.toFixed(2)} {produs.toLowerCase().includes('vign') ? 'buc' : 'L'}
                </span>
                <span className="text-xs text-slate-500 block">{data.cost?.toFixed(2)} lei</span>
              </div>
            ))}
            <div className="bg-blue-100 px-3 py-2 rounded-md border border-blue-300">
              <span className="text-xs text-blue-600 block">TOTAL</span>
              <span className="text-lg font-bold text-blue-800">{filteredTotals.totalLitri?.toFixed(2)} L</span>
              <span className="text-xs text-blue-600 block">{filteredTotals.totalCost?.toFixed(2)} lei</span>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">
          <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin text-slate-300" />
          Se încarcă...
        </div>
      ) : filteredImports.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Fuel className="w-12 h-12 mx-auto mb-2 text-slate-300" />
          <p className="font-medium">Nu există alimentări</p>
          <p className="text-sm mt-1">Alimentările vor apărea aici când șoferii le înregistrează sau când importi un CSV</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-[110px]">Data</TableHead>
                  <TableHead className="w-[50px]">Ora</TableHead>
                  <TableHead className="w-[100px]">Nr. Înmatr.</TableHead>
                  <TableHead className="w-[95px]">Sursa</TableHead>
                  <TableHead className="w-[100px]">Produs</TableHead>
                  <TableHead className="w-[70px]">Cant.</TableHead>
                  <TableHead className="w-[80px]">Cost</TableHead>
                  <TableHead className="w-[70px]">Km</TableHead>
                  <TableHead>Stație / Șofer</TableHead>
                  <TableHead className="w-[90px]">Nr. Bon</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[200px]">Alocare</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredImports.map((imp) => {
                  const statusConfig = STATUS_CONFIG[imp.status] || STATUS_CONFIG.ok;
                  const StatusIcon = statusConfig.icon;
                  const isVigneta = imp.produs?.toLowerCase().includes('vign');
                  const canReallocate = imp.type === 'csv'; // Orice import CSV poate fi re-alocat
                  const showAllocateDropdown = canReallocate && (imp.status === 'nealocat' || imp.status === 'dublat' || imp.status === 'ok' || imp.status === 'reconciliat');
                  
                  return (
                  <TableRow 
                    key={imp.id} 
                    className={
                      imp.status === 'nealocat' ? 'bg-orange-50/50' : 
                      imp.status === 'reconciliat' ? 'bg-blue-50/50' : ''
                    }
                  >
                    <TableCell className="text-sm font-medium whitespace-nowrap">
                      {imp.data_alimentare_date}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {imp.ora_alimentare}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {imp.nr_inmatriculare || imp.car_plate}
                    </TableCell>
                    <TableCell>
                      {imp.status === 'reconciliat' && imp.sofer_name ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          <CheckCircle className="w-3 h-3" />
                          CSV + {imp.sofer_name}
                        </span>
                      ) : (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          imp.sursa === 'Șofer' ? 'bg-purple-100 text-purple-700' : 'bg-cyan-100 text-cyan-700'
                        }`}>
                          {imp.sursa === 'Șofer' ? <User className="w-3 h-3" /> : <FileSpreadsheet className="w-3 h-3" />}
                          {imp.sursa}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {imp.produs}
                    </TableCell>
                    <TableCell className="text-sm font-semibold text-green-700">
                      {imp.litri?.toFixed(2)} {isVigneta ? 'buc' : 'L'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {imp.cost?.toFixed(2)} lei
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {imp.km?.toLocaleString() || '-'}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 max-w-[120px] truncate" title={imp.statie || imp.sofer_name}>
                      {imp.statie || imp.sofer_name || '-'}
                    </TableCell>
                    <TableCell className="text-xs font-mono text-slate-500" title={imp.bon || ''}>
                      {imp.type === 'csv' ? (imp.bon || '-') : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig.label}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {showAllocateDropdown ? (
                        <div className="flex items-center gap-1">
                          <Select 
                            value={selectedCarId[imp.id] || ''} 
                            onValueChange={(v) => setSelectedCarId(prev => ({ ...prev, [imp.id]: v }))}
                          >
                            <SelectTrigger className="w-[130px] h-8 text-xs">
                              <SelectValue placeholder={imp.car_name || "Selectează"} />
                            </SelectTrigger>
                            <SelectContent>
                              {cars.map(car => (
                                <SelectItem key={car.id} value={car.id} className="text-xs">
                                  {car.license_plate} - {car.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button 
                            size="sm" 
                            className="h-8 px-2"
                            onClick={() => handleAllocate(imp.id)}
                            disabled={!selectedCarId[imp.id]}
                            title={imp.car_id ? "Re-alocă" : "Alocă"}
                          >
                            <Check className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-500">
                          {imp.car_name || imp.sofer_name || '-'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {imp.type === 'csv' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteImport(imp.id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview Import CSV</DialogTitle>
            <DialogDescription>
              Verifică datele înainte de import
            </DialogDescription>
          </DialogHeader>
          
          {csvPreviewData && (
            <div className="space-y-4">
              {/* Summary - clickable filters */}
              <div className="grid grid-cols-4 gap-3">
                <button
                  onClick={() => setPreviewFilter(null)}
                  className={`text-left p-3 rounded border-2 transition-colors ${!previewFilter ? 'bg-slate-200 border-slate-400' : 'bg-slate-100 border-transparent hover:border-slate-300'}`}
                  data-testid="filter-all"
                >
                  <div className="text-xs text-slate-500">Total</div>
                  <div className="text-xl font-bold">{csvPreviewData.summary?.total || 0}</div>
                </button>
                <button
                  onClick={() => setPreviewFilter('ready')}
                  className={`text-left p-3 rounded border-2 transition-colors ${previewFilter === 'ready' ? 'bg-green-200 border-green-400' : 'bg-green-100 border-transparent hover:border-green-300'}`}
                  data-testid="filter-ready"
                >
                  <div className="text-xs text-green-600">Ready</div>
                  <div className="text-xl font-bold text-green-700">{csvPreviewData.summary?.ready || 0}</div>
                </button>
                <button
                  onClick={() => setPreviewFilter('car_not_found')}
                  className={`text-left p-3 rounded border-2 transition-colors ${previewFilter === 'car_not_found' ? 'bg-orange-200 border-orange-400' : 'bg-orange-100 border-transparent hover:border-orange-300'}`}
                  data-testid="filter-not-found"
                >
                  <div className="text-xs text-orange-600">Mașini negăsite</div>
                  <div className="text-xl font-bold text-orange-700">{csvPreviewData.summary?.errors || 0}</div>
                </button>
                <button
                  onClick={() => setPreviewFilter('duplicate')}
                  className={`text-left p-3 rounded border-2 transition-colors ${previewFilter === 'duplicate' ? 'bg-yellow-200 border-yellow-400' : 'bg-yellow-100 border-transparent hover:border-yellow-300'}`}
                  data-testid="filter-duplicate"
                >
                  <div className="text-xs text-yellow-600">Duplicate</div>
                  <div className="text-xl font-bold text-yellow-700">{csvPreviewData.summary?.duplicates || 0}</div>
                </button>
              </div>
              
              {previewFilter && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <span>Filtru activ: <strong>{previewFilter === 'ready' ? 'Ready' : previewFilter === 'car_not_found' ? 'Mașini negăsite' : 'Duplicate'}</strong></span>
                  <button onClick={() => setPreviewFilter(null)} className="text-red-500 hover:text-red-700 underline text-xs">Șterge filtru</button>
                </div>
              )}
              
              {/* Preview table */}
              <div className="border rounded max-h-[400px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Nr. Înmatr.</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Produs</TableHead>
                      <TableHead>Litri</TableHead>
                      <TableHead>Cost</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {csvPreviewData.preview
                      ?.filter(item => !previewFilter || item.status === previewFilter)
                      .map((item, idx) => (
                      <TableRow key={idx} className={
                        item.status === 'ready' ? 'bg-green-50/50' :
                        item.status === 'car_not_found' ? 'bg-orange-50/50' :
                        item.status === 'duplicate' ? 'bg-yellow-50/50' : ''
                      }>
                        <TableCell className="font-mono text-sm">{item.license_plate}</TableCell>
                        <TableCell className="text-sm">{item.date}</TableCell>
                        <TableCell className="text-xs">{item.product}</TableCell>
                        <TableCell>{item.quantity?.toFixed(2)}</TableCell>
                        <TableCell>{item.cost?.toFixed(2)} lei</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.status === 'ready' ? 'bg-green-100 text-green-700' :
                            item.status === 'car_not_found' ? 'bg-orange-100 text-orange-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {item.status === 'ready' ? 'OK' : 
                             item.status === 'car_not_found' ? 'Mașină negăsită' : 'Duplicat'}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {!previewFilter && csvPreviewData.preview?.length > 50 && (
                  <div className="p-2 text-center text-sm text-slate-500 bg-slate-50">
                    ... și încă {csvPreviewData.preview.length - 50} înregistrări
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Anulează
            </Button>
            <Button 
              onClick={handleConfirmImport} 
              disabled={uploading}
              className="bg-green-600 hover:bg-green-700"
            >
              {uploading ? 'Se importă...' : 'Confirmă Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch History Dialog */}
      <Dialog open={showBatchDialog} onOpenChange={setShowBatchDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Istoric Importuri</DialogTitle>
            <DialogDescription>
              Vizualizează și anulează importurile anterioare
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {!Array.isArray(batches) || batches.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                Nu există importuri înregistrate
              </div>
            ) : (
              batches.map(batch => (
                <div key={batch.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <div className="font-medium text-sm">
                      {new Date(batch.imported_at).toLocaleDateString('ro-RO', {
                        day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </div>
                    <div className="text-xs text-slate-500">
                      {batch.items_count} înregistrări • De: {batch.imported_by}
                    </div>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteBatch(batch.id)}
                    disabled={deletingBatch === batch.id}
                  >
                    {deletingBatch === batch.id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-1" />
                        Anulează
                      </>
                    )}
                  </Button>
                </div>
              ))
            )}
          </div>
          
          <DialogFooter className="flex justify-between sm:justify-between gap-2">
            <div className="flex flex-wrap gap-2">
              <Button 
                variant="outline"
                className="text-purple-600 border-purple-300 hover:bg-purple-50"
                onClick={handleClearVerified}
                disabled={(stats.dublat || 0) + (stats.verificat || 0) === 0}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Șterge Dublate + Verificate ({(stats.dublat || 0) + (stats.verificat || 0)})
              </Button>
              <Button 
                variant="outline"
                onClick={handleNormalizeProducts}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Normalizează Produse
              </Button>
            </div>
            <Button variant="outline" onClick={() => setShowBatchDialog(false)}>
              Închide
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default AlimentariImportTab;
